/**
 * Publishing this screen to a club.
 *
 * **It used to do two jobs and was named for neither clearly.** One half listed
 * the club and product screens with an Apply that *copied* one into the panel;
 * the other published the panel as a club screen. Once a club's screen became a
 * tab in its own right — linked, with the member's own layer over it — the list
 * was a second place the same screens appeared, offering a third thing to do
 * with them, under a word ("Presets") that named neither act.
 *
 * So the copy moved to where its object is: a **Duplicate** on the club tab
 * itself, which takes what is on screen — their half and yours — into a screen
 * of your own. What is left here is publishing, and the tab says so.
 *
 * ## Applying copies; it never links
 *
 * A preset arrives as a definition and is written into **this** section's
 * config through the ordinary `onConfigChange` patch. So it is an edit the user
 * can then change, and undo, like any other — where a linked view would be a
 * screen they cannot touch, and every later edit by the club would silently
 * rearrange a screen they had made their own.
 *
 * The cost, stated rather than hidden: a preset edited after somebody applied
 * it does not reach them. That is the same trade the layout templates make.
 *
 * ## The island owns no transport
 *
 * `publishPreset` is injected, exactly as
 * `compileExpr` and `describeExpr` are. Omit them and the tab is not offered —
 * which is what a build with no server configured gets, and what a user who is
 * not in a club sees for the publish half.
 */

import { useState, type ReactNode } from "react";
import type { ScreenerConfig } from "@qufolio/model";


/** One preset as the picker lists it — no column set until it is chosen. */
export interface PresetSummary {
  key: string;
  name: string;
  description: string | null;
  /**
   * Who owns it. `user` is the caller's **own** screen — a tab rather than
   * something to apply — so the host filters those out before they reach this
   * list. The type carries it anyway because one listing serves both surfaces,
   * and a narrower type here would make the host cast rather than filter.
   */
  ownerKind: "system" | "org" | "user";
  ownerKey: string | null;
  /**
   * A group's display name (`JP`), on a group's screen only — what its tab's
   * chip reads, since `ownerKey` is `grp-jp`. Absent from an older server.
   */
  ownerName?: string;
  /** A group's published screen: its current version. Absent otherwise. */
  version?: number;
  /**
   * The screen's conditions reach members as name and category only, and the
   * server evaluates them (`docs/plans/screen-links.md`). Per screen, and on for
   * JP and JAS. Absent from an older server, which means open. The entries
   * themselves say `sealed: true` — this is the screen's setting, read for the
   * tab rather than for evaluating anything.
   */
  sealedConditions?: boolean;
  market: string;
  groupCodes: string[];
}

/**
 * The whole definition, fetched when one is applied.
 *
 * Spelled in **contract** names — `filter`, `sort` — not the store's
 * (`base_filter`, `sorts`). The wire mapping belongs in the host's api module,
 * for the same reason this island takes `compileExpr` rather than a URL: it
 * should not know what the server calls its columns.
 */
export interface PresetDefinition extends PresetSummary {
  columns: ScreenerConfig["columns"];
  filter: ScreenerConfig["filter"];
  sort: ScreenerConfig["sort"];
  /**
   * The named condition library, and the one field here where **`null` is not
   * `[]`**: null means a screen stored before conditions travelled with one, so
   * the caller keeps the library already on its panel. An empty array means
   * somebody deleted the last condition. Collapsing the two empties the
   * conditions popup on every screen that predates the column.
   */
  savedFilters?: ScreenerConfig["savedFilters"] | null;
  /** The order the picker lists the library's categories in; null is A–Z. */
  savedFilterCategoryOrder?: ScreenerConfig["savedFilterCategoryOrder"] | null;
  /** A JP trade-list screen (`docs/plans/jp-recs.md`); absent is ordinary. */
  recs?: ScreenerConfig["recs"] | null;
  /** A TopDown screen (`docs/features/topdown.md`); absent is ordinary. */
  topDown?: ScreenerConfig["topDown"] | null;
  /** A Seasonality screen (`docs/features/seasonality.md`); absent is ordinary. */
  seasonality?: ScreenerConfig["seasonality"] | null;
  /** The bar's ▲▼ key as saved with the screen; absent is All. */
  direction?: ScreenerConfig["direction"];
}

export interface PresetsTabProps {
  config: ScreenerConfig;
  /**
   * Publishes the current screen. Offered only when supplied — the host passes
   * it for an admin and omits it for everybody else, so the control cannot be
   * shown to somebody the server would refuse.
   */
  publishPreset?: (input: {
    name: string;
    description: string;
    columns: ScreenerConfig["columns"];
    filter: ScreenerConfig["filter"];
    sort: ScreenerConfig["sort"];
    /** A Recs or TopDown screen keeps being one when published (omitted: ordinary). */
    recs?: ScreenerConfig["recs"];
    topDown?: ScreenerConfig["topDown"];
    seasonality?: ScreenerConfig["seasonality"];
  }) => Promise<void>;
  /**
   * What the group has already published, with each one's history — drawn by
   * the host, because the history, Restore and Withdraw talk to an admin API
   * this island does not know (`docs/plans/group-accounts.md` §5). Publishing
   * again under a published name is the update.
   */
  publishedSlot?: ReactNode;
}

export function PresetsTab({
  config,
  publishPreset,
  publishedSlot,
}: PresetsTabProps) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [draftName, setDraftName] = useState("");
  const [draftDescription, setDraftDescription] = useState("");

  const publish = async () => {
    if (!publishPreset) return;
    if (!draftName.trim()) {
      setError("A preset needs a name.");
      return;
    }
    setBusy("__publish");
    setError(null);
    setNote(null);
    try {
      await publishPreset({
        name: draftName.trim(),
        description: draftDescription.trim(),
        columns: config.columns ?? [],
        filter: config.filter,
        sort: config.sort,
        ...(config.recs ? { recs: config.recs } : {}),
        ...(config.topDown ? { topDown: config.topDown } : {}),
        ...(config.seasonality ? { seasonality: config.seasonality } : {}),
      });
      setDraftName("");
      setDraftDescription("");
      setNote("Published. Members of the club will see it in this list.");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div style={PANE}>
      {error && <div style={ERROR}>{error}</div>}
      {note && <div style={NOTE}>{note}</div>}

      {publishPreset && (
        <>
          <div style={HEADING}>Publish this screen</div>
          <div style={NOTE}>
            Takes the columns, filter and sorts as they are right now. Publishing
            under a name already published replaces it for every member.
          </div>
          <div style={ROW}>
            <input
              style={{ ...INPUT, flex: "1 1 auto" }}
              placeholder="name"
              value={draftName}
              onChange={(event) => setDraftName(event.target.value)}
            />
            <button
              type="button"
              style={BUTTON}
              disabled={busy !== null}
              onClick={() => void publish()}
            >
              {busy === "__publish" ? "Publishing…" : "Publish"}
            </button>
          </div>
          <input
            style={INPUT}
            placeholder="description (optional)"
            value={draftDescription}
            onChange={(event) => setDraftDescription(event.target.value)}
          />
          {publishedSlot}
        </>
      )}
    </div>
  );
}

const PANE: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
  padding: 10,
  overflowY: "auto",
};

const ROW: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "4px 0",
};

const HEADING: React.CSSProperties = {
  marginTop: 10,
  paddingTop: 8,
  borderTop: "1px solid var(--qf-border)",
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: 0.6,
  color: "var(--qf-text-muted)",
};

const NOTE: React.CSSProperties = {
  color: "var(--qf-text-secondary)",
  fontSize: 12,
  lineHeight: 1.5,
};

const ERROR: React.CSSProperties = { ...NOTE, color: "var(--qf-negative, #e5484d)" };

const BUTTON: React.CSSProperties = {
  flex: "0 0 auto",
  background: "var(--qf-surface-alt)",
  color: "var(--qf-text)",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  padding: "4px 10px",
  font: "inherit",
  cursor: "pointer",
};

const INPUT: React.CSSProperties = {
  background: "var(--qf-surface-alt)",
  color: "var(--qf-text)",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  padding: "4px 6px",
  font: "inherit",
  minWidth: 0,
};
